import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Seo from "@/components/Seo";

const TILES = [
  { img: "/gallery/g1.webp", tag: "Rider / Hoodie S'NO noir", ratio: "aspect-[4/3]" },
  { img: "/gallery/g2.webp", tag: "Duo / Session sommet", ratio: "aspect-[16/9]" },
  { img: "/gallery/g3.webp", tag: "Rideuse / Drop rose", ratio: "aspect-square" },
  { img: "/gallery/g4.webp", tag: "Summit / Hoodie blanc", ratio: "aspect-[4/3]" },
  { img: "/gallery/g5.webp", tag: "Chalet / Manches longues", ratio: "aspect-[3/2]" },
];

export default function Gallery() {
  const [selected, setSelected] = useState(null);

  return (
    <div className="px-4 sm:px-6 lg:px-12 pt-32 pb-24" data-testid="gallery-page">
      <Seo
        title="Galerie — S'NO"
        siteName="S'NO"
        description="Lookbook S'NO : riders, poudreuse, textures et graffitis. Le moodboard urbain et montagnard de la marque."
        image="/logo.webp"
      />

      <p className="font-mono text-xs uppercase tracking-[0.4em] text-ice mb-3">Lookbook / Moodboard</p>
      <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-none mb-6">
        La galerie
      </h1>
      <p className="text-sm text-zinc-400 max-w-xl leading-relaxed mb-14">
        Sessions de snow, murs tagués, détails de matière. L'univers S'NO en images —
        clique pour agrandir.
      </p>

      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4" data-testid="gallery-masonry">
        {TILES.map((tile, i) => (
          <motion.button
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            onClick={() => setSelected(tile)}
            data-testid={`gallery-tile-${i}`}
            className={`relative w-full ${tile.ratio} mb-4 break-inside-avoid overflow-hidden border border-white/10 group cursor-zoom-in`}
          >
            <img
              src={tile.img}
              alt={tile.tag}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500" />
            <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ice bg-black/70 backdrop-blur px-2 py-1 border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              {tile.tag}
            </span>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/95 backdrop-blur flex items-center justify-center p-4 sm:p-10"
            onClick={() => setSelected(null)}
            data-testid="gallery-lightbox"
          >
            <button
              data-testid="lightbox-close-button"
              className="absolute top-5 right-5 p-3 border border-white/25 text-white hover:border-ice hover:text-ice transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selected.img}
                alt={selected.tag}
                className="w-full max-h-[78vh] object-contain border border-white/10"
              />
              <figcaption className="font-mono text-xs uppercase tracking-[0.3em] text-ice mt-4 text-center">
                {selected.tag}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
